import React, { useEffect, useState, useRef } from "react"
import { db, auth, arrayUnion, arrayRemove } from "../../../utils/firebase"
import { PlusIcon } from "../../../utils/svgs"

import WindowCard from "../../../components/ui/window-card/window-card"
import { logActivity } from "./activity/log"
import { normaliseTag } from "../../../utils/tags"

/**
 * Right-rail card for managing custom tags on an enquiry. Tag library is
 * stored per-user under users/{uid}.tag_library so each agent builds up
 * their own autocomplete pool.
 */
export default function TagsCard({ enquiryID }) {
	const [tags, setTags] = useState([])
	const [library, setLibrary] = useState([])
	const [adding, setAdding] = useState(false)
	const [draft, setDraft] = useState("")
	const inputRef = useRef(null)

	const uid = auth.currentUser?.uid

	useEffect(() => {
		if (!enquiryID) return
		const unsub = db.doc(`enquiries/${enquiryID}`).onSnapshot((doc) => {
			if (!doc.exists) return
			setTags(doc.data().tags || [])
		})
		return () => unsub()
	}, [enquiryID])

	useEffect(() => {
		if (!uid) return
		const unsub = db.doc(`users/${uid}`).onSnapshot((doc) => {
			if (!doc.exists) return
			setLibrary(doc.data().tag_library || [])
		})
		return () => unsub()
	}, [uid])

	useEffect(() => {
		if (adding && inputRef.current) inputRef.current.focus()
	}, [adding])

	const addTag = async (raw) => {
		const tag = normaliseTag(raw || "")
		if (!tag) return
		if (tags.includes(tag)) {
			setDraft("")
			setAdding(false)
			return
		}

		await db.doc(`enquiries/${enquiryID}`).set({ tags: arrayUnion(tag) }, { merge: true })

		if (uid && !library.includes(tag)) {
			await db.doc(`users/${uid}`).set({ tag_library: arrayUnion(tag) }, { merge: true })
		}

		await logActivity(`enquiries/${enquiryID}`, "TAG_ADDED", `Tagged "${tag}"`)

		setDraft("")
		setAdding(false)
	}

	const removeTag = async (tag) => {
		await db.doc(`enquiries/${enquiryID}`).set({ tags: arrayRemove(tag) }, { merge: true })
		await logActivity(`enquiries/${enquiryID}`, "TAG_REMOVED", `Removed tag "${tag}"`)
	}

	const onKeyDown = (e) => {
		if (e.key === "Enter") {
			e.preventDefault()
			addTag(draft)
		}
		if (e.key === "Escape") {
			setDraft("")
			setAdding(false)
		}
	}

	const suggestions = draft
		? library.filter((t) => t.includes(draft.toLowerCase()) && !tags.includes(t)).slice(0, 6)
		: library.filter((t) => !tags.includes(t)).slice(0, 6)

	return (
		<WindowCard title="Tags">
			<div className="enquiry-card-body">
				<div className="enquiry-tags-row">
					{tags.length === 0 && !adding && (
						<small className="no-value">No tags yet</small>
					)}

					{tags.map((tag) => (
						<span className="enquiry-tag" key={tag}>
							{tag}
							<button
								type="button"
								className="enquiry-tag-remove"
								aria-label={`Remove ${tag}`}
								onClick={() => removeTag(tag)}>
								×
							</button>
						</span>
					))}

					{!adding && (
						<button
							type="button"
							className="enquiry-tag-add"
							onClick={() => setAdding(true)}>
							<PlusIcon />
							<span>Add</span>
						</button>
					)}
				</div>

				{adding && (
					<div className="enquiry-tags-composer">
						<input
							ref={inputRef}
							className="enquiry-tag-input"
							type="text"
							placeholder="Type a tag and press Enter"
							value={draft}
							onChange={(e) => setDraft(e.target.value)}
							onKeyDown={onKeyDown}
							onBlur={() => {
								// Defer so a click on a suggestion can still register
								setTimeout(() => {
									if (!draft) setAdding(false)
								}, 120)
							}}
						/>
						{suggestions.length > 0 && (
							<div className="enquiry-tag-suggestions">
								{suggestions.map((s) => (
									<button
										key={s}
										type="button"
										className="enquiry-tag-suggestion"
										onMouseDown={(e) => e.preventDefault()}
										onClick={() => addTag(s)}>
										{s}
									</button>
								))}
							</div>
						)}
					</div>
				)}
			</div>
		</WindowCard>
	)
}
